import React, { useState, useEffect } from 'react';
import './PhoneVueFunctions.css';

// Import Functional Diagnosis icons
import ScreenTestIcon from '../../assets/images/funcDiagnosis/ScreenTest.svg';
import ChargingTestIcon from '../../assets/images/funcDiagnosis/ChargingTest.svg';
import RearCameraIcon from '../../assets/images/funcDiagnosis/RearCamera.svg';
import FrontCameraIcon from '../../assets/images/funcDiagnosis/FrontCamera.svg';
import CellularNetworkIcon from '../../assets/images/funcDiagnosis/CellularNetwork.svg';
import SpeakerIcon from '../../assets/images/funcDiagnosis/Speaker.svg';
import ProximityIcon from '../../assets/images/funcDiagnosis/Proximity.svg';
import MicrophoneIcon from '../../assets/images/funcDiagnosis/Microphone.svg';
import BluetoothIcon from '../../assets/images/funcDiagnosis/Bluetooth.svg';
import VibratorIcon from '../../assets/images/funcDiagnosis/Vibrator.svg';
import FlashIcon from '../../assets/images/funcDiagnosis/Flash.svg';
import WiFiIcon from '../../assets/images/funcDiagnosis/Wi-FI.svg';
import AccelerometerIcon from '../../assets/images/funcDiagnosis/Accelerometer.svg';
import GyroscopeIcon from '../../assets/images/funcDiagnosis/Gyroscope.svg';
import GPSIcon from '../../assets/images/funcDiagnosis/GPS.svg';
import ButtonHapticsIcon from '../../assets/images/funcDiagnosis/ButtonHaptics.svg';
import WirelessChargingIcon from '../../assets/images/funcDiagnosis/WirelessCharging.svg';
import DeviceSpecificationIcon from '../../assets/images/funcDiagnosis/Device Specification.svg';
import CPUIcon from '../../assets/images/funcDiagnosis/CPU.svg';
import MemoryIcon from '../../assets/images/funcDiagnosis/Memory.svg';
import StorageIcon from '../../assets/images/funcDiagnosis/Storage.svg';
import RootTestIcon from '../../assets/images/funcDiagnosis/Root Test.svg';
import NetworkStatsIcon from '../../assets/images/funcDiagnosis/Network Stats.svg';
import TouchPanelIcon from '../../assets/images/funcDiagnosis/Touch Panel.svg';

// Import Terminal Function icons
import CallStateIcon from '../../assets/images/terminalFunc/Call state result.svg';
import CommunicationStandardIcon from '../../assets/images/terminalFunc/Communication standard results.svg';
import DataStateIcon from '../../assets/images/terminalFunc/Data state result.svg';
import NetworkRoamingIcon from '../../assets/images/terminalFunc/Network roaming results.svg';

// Import Cellular Network icons
import CarrierNameIcon from '../../assets/images/cellularNetwork/Carrier name results.svg';
import CountryCodeIcon from '../../assets/images/cellularNetwork/Country code result.svg';
import NetworkTypeIcon from '../../assets/images/cellularNetwork/Network type result.svg';
import SIMRemainingIcon from '../../assets/images/cellularNetwork/SIM remaining confirmation result.svg';

const PhoneVueFunctions = () => {
  const [activeTab, setActiveTab] = useState('functional');

  const functionalFunctions = [
    { id: 1, name: 'Screen Test', icon: ScreenTestIcon, description: 'It is a test to diagnose whether the screen is working.' },
    { id: 2, name: 'Charging Test', icon: ChargingTestIcon, description: 'This test checks if your device charging port and charging functionality is working properly.' },
    { id: 3, name: 'Rear Camera', icon: RearCameraIcon, description: 'This is a test to diagnose whether the back camera is working. Take a picture and check it. Picture is not saved in device, but testing camera is functioning properly.' },
    { id: 4, name: 'Front camera', icon: FrontCameraIcon, description: 'This test checks if your front camera is functioning correctly. Take a selfie to verify camera functionality.' },
    { id: 5, name: 'Cellular Network', icon: CellularNetworkIcon, description: 'Test to verify cellular network connectivity and signal strength on your device.' },
    { id: 6, name: 'Speaker', icon: SpeakerIcon, description: 'Diagnostic test to check if the device speaker is working and producing sound correctly.' },
    { id: 7, name: 'Proximity', icon: ProximityIcon, description: 'Test to verify the proximity sensor functionality, which detects when objects are near the screen.' },
    { id: 8, name: 'Microphone', icon: MicrophoneIcon, description: 'Test to check if the microphone is working properly and can record audio correctly.' },
    { id: 9, name: 'Bluetooth', icon: BluetoothIcon, description: 'Diagnostic test to verify Bluetooth connectivity and pairing functionality on your device.' },
    { id: 10, name: 'Vibration', icon: VibratorIcon, description: 'Test to check if the vibration motor is working correctly on your device.' },
    { id: 11, name: 'Flash', icon: FlashIcon, description: 'Test to verify if the camera flash LED is functioning properly.' },
    { id: 12, name: 'Wi-Fi', icon: WiFiIcon, description: 'Diagnostic test to check Wi-Fi connectivity and signal strength on your device.' },
    { id: 13, name: 'Accelerometer', icon: AccelerometerIcon, description: 'Test to verify the accelerometer sensor which detects device orientation and movement.' },
    { id: 14, name: 'Gyroscope', icon: GyroscopeIcon, description: 'Test to check the gyroscope sensor functionality for rotation and orientation detection.' },
    { id: 15, name: 'GPS', icon: GPSIcon, description: 'Diagnostic test to verify GPS functionality and location detection accuracy.' },
    { id: 16, name: 'Button & Haptics', icon: ButtonHapticsIcon, description: 'Test to check if physical buttons and haptic feedback are working correctly.' },
    { id: 17, name: 'Wireless Charging', icon: WirelessChargingIcon, description: 'Test to verify wireless charging functionality if your device supports it.' },
    { id: 18, name: 'Device Specific.', icon: DeviceSpecificationIcon, description: 'Test to check device-specific features and specifications unique to your device model.' },
    { id: 19, name: 'CPU', icon: CPUIcon, description: 'Diagnostic test to check CPU performance, temperature, and processing capabilities.' },
    { id: 20, name: 'Memory', icon: MemoryIcon, description: 'Test to verify RAM functionality and memory performance on your device.' },
    { id: 21, name: 'Storage', icon: StorageIcon, description: 'Diagnostic test to check internal storage capacity and read/write functionality.' },
    { id: 22, name: 'Root Test', icon: RootTestIcon, description: 'Test to check if your device has root access or is running in a modified state.' },
    { id: 23, name: 'Network Stats', icon: NetworkStatsIcon, description: 'Test to display network statistics including data usage, connection quality, and network performance.' },
    { id: 24, name: 'Touch panel', icon: TouchPanelIcon, description: 'Test to diagnose touch screen functionality and verify all touch points are responding correctly.' }
  ];

  const terminalFunctions = [
    { id: 1, name: 'Call state result', icon: CallStateIcon, description: 'Test to check the current call state of your device, including idle, ringing, or in-call status.' },
    { id: 2, name: 'Communication standard results', icon: CommunicationStandardIcon, description: 'Diagnostic test to verify the communication standards supported by your device (2G, 3G, 4G, 5G).' },
    { id: 3, name: 'Data state result', icon: DataStateIcon, description: 'Test to check the current data connection state, including connected, disconnected, or connecting status.' },
    { id: 4, name: 'Network roaming results', icon: NetworkRoamingIcon, description: 'Test to verify if your device is currently roaming on a different network carrier.' }
  ];

  const cellularFunctions = [
    { id: 1, name: 'Carrier name results', icon: CarrierNameIcon, description: 'Test to display the name of your current mobile network carrier or service provider.' },
    { id: 2, name: 'Country code result', icon: CountryCodeIcon, description: 'Test to show the country code of your current network location and SIM card.' },
    { id: 3, name: 'Network type result', icon: NetworkTypeIcon, description: 'Test to identify the type of network connection (LTE, HSPA+, EDGE, etc.) currently active on your device.' },
    { id: 4, name: 'SIM remaining confirmation result', icon: SIMRemainingIcon, description: 'Test to check and display the remaining balance or data quota information from your SIM card.' }
  ];

  const getCurrentFunctions = () => {
    switch (activeTab) {
      case 'terminal':
        return terminalFunctions;
      case 'cellular':
        return cellularFunctions;
      default:
        return functionalFunctions;
    }
  };

  const functions = getCurrentFunctions();
  const [selectedFunction, setSelectedFunction] = useState('Screen Test');

  useEffect(() => {
    // Update selected function when tab changes
    const currentFunctions = getCurrentFunctions();
    if (currentFunctions.length > 0) {
      setSelectedFunction(currentFunctions[0].name);
    }
  }, [activeTab]);

  return (
    <div className="phonevue-functions-page">
      <div className="phonevue-functions-container">
        <div className="functions-header">
          <p className="test-modules-label">Test Modules</p>
          <h1 className="functions-title">PhoneVue Function</h1>
        </div>

        <div className="functions-tabs">
          <button 
            className={`tab-button ${activeTab === 'functional' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('functional');
              setSelectedFunction('Screen Test');
            }}
          >
            Functional Diagnosis
          </button>
          <button 
            className={`tab-button ${activeTab === 'terminal' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('terminal');
              setSelectedFunction(terminalFunctions[0]?.name || '');
            }}
          >
            Terminal Function *
          </button>
          <button 
            className={`tab-button ${activeTab === 'cellular' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('cellular');
              setSelectedFunction(cellularFunctions[0]?.name || '');
            }}
          >
            Cellular Network
          </button>
        </div>

        <div className="functions-wrapper">
          <div className="functions-grid">
            {functions.map((func) => (
              <div key={func.id} className="function-button-wrapper">
                <button
                  className={`function-button ${selectedFunction === func.name ? 'selected' : ''}`}
                  onMouseEnter={() => setSelectedFunction(func.name)}
                >
                  <div className="function-icon">
                    <img src={func.icon} alt={func.name} />
                  </div>
                  <div className="function-name">{func.name}</div>
                </button>
                {selectedFunction === func.name && func.description && (
                  <div className="function-tooltip">
                    <p className="tooltip-text">{func.description}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneVueFunctions;
